import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Formik, Form, getIn } from 'formik';

import { Input, SingleSelect } from '@/common';
import { Button, Spinner } from '@/components/generic';
import { ButtonVariant } from '@/components/generic/Button/button-config';
import { queryClient } from '@/configuration/react-query';
import { PRIVATE_PAGE_ROUTES } from '@/enums';
import { getLanguageOptionsForServiceMessage } from '@/helpers';
import { showToast, ToastType } from '@/helpers/toast';
import {
  useCreateServiceMessageMutation,
  useDeleteServiceMessageMutation,
  useGetServiceMessage,
  useUpdateServiceMessageMutation
} from '@/services/serviceMessage.service';
import { SelectOptionProps } from '@/types';
import {
  CreateEditServiceMessage,
  ServiceMessage,
  ServiceMessageValue
} from '@/types/ServiceMessageTypes';

import { createEditServiceMessageSchema } from './Validation';

export const Settings = () => {
  const [t] = useTranslation('common');
  const navigate = useNavigate();
  const { data: serviceMessage, isLoading } = useGetServiceMessage();

  const backgroundColorOptions: SelectOptionProps[] = [
    { value: '#FF4D4F', color: '#FF4D4F', label: t('red') },
    { value: '#1890FF', color: '#1890FF', label: t('blue') },
    { value: '#52C41A', color: '#52C41A', label: t('green') },
    { value: '#FAAD14', color: '#FAAD14', label: t('yellow') },
    { value: '#722ED1', color: '#722ED1', label: t('purple') }
  ];

  const buildInitialValues = (serviceMessage?: ServiceMessage) => {
    const initialValues = {
      backgroundColor: serviceMessage?.backgroundColor ?? '',
      values: {} as Record<string, string>
    };

    getLanguageOptionsForServiceMessage().forEach(({ code }) => {
      const existingValue = serviceMessage?.values?.find((v) => v.languageCode === code);
      initialValues.values[code] = existingValue?.message ?? '';
    });

    return initialValues;
  };

  const initialValues = buildInitialValues(serviceMessage?.data);

  const { mutate: createServiceMessage } = useCreateServiceMessageMutation(queryClient, {
    onSuccess: () => {
      navigate(PRIVATE_PAGE_ROUTES.EVENTS);
      showToast(ToastType.SUCCESS, t('serviceMessageCreated'));
    },
    onError: (error) => {
      showToast(ToastType.ERROR, error.response.data.ErrorMessages[0].DisplayMessage);
    }
  });

  const deleteServiceMessageMutation = useDeleteServiceMessageMutation(queryClient, {
    onSuccess: () => {
      navigate(PRIVATE_PAGE_ROUTES.EVENTS);
      showToast(ToastType.SUCCESS, t('serviceMessageRemoved'));
    },
    onError: (error) => {
      showToast(ToastType.ERROR, error.response.data.ErrorMessages[0].DisplayMessage);
    }
  });

  const { mutate: updateServiceMessage } = useUpdateServiceMessageMutation(queryClient, {
    onSuccess: () => {
      navigate(PRIVATE_PAGE_ROUTES.EVENTS);
      showToast(ToastType.SUCCESS, t('serviceMessageUpdated'));
    },
    onError: (error) => {
      showToast(ToastType.ERROR, error.response.data.ErrorMessages[0].DisplayMessage);
    }
  });

  const handleCreateMessage = (values: CreateEditServiceMessage) => {
    createServiceMessage(values);
  };

  const handleRemoveMessage = () => {
    if (!serviceMessage?.data?.id) {
      return;
    }
    deleteServiceMessageMutation.mutate(serviceMessage.data.id);
  };

  const handleEditMessage = (values: CreateEditServiceMessage) => {
    updateServiceMessage({ ...values, id: serviceMessage?.data.id });
  };
  return (
    <div className='flex w-full flex-col p-6 px-12'>
      <div className='mb-8'>
        <h1 className='text-3xl font-bold text-primary-blue'>{t('settings')}</h1>
      </div>

      {isLoading ? (
        <Spinner />
      ) : (
        <Formik
          initialValues={initialValues}
          validationSchema={createEditServiceMessageSchema}
          onSubmit={(values) => {
            const payload: CreateEditServiceMessage = {
              backgroundColor: values.backgroundColor,
              values: Object.entries(values.values).map(([languageCode, message]) => {
                const existing = serviceMessage?.data?.values?.find(
                  (v: ServiceMessageValue) => v.languageCode === languageCode
                );
                return {
                  id: existing?.id,
                  languageCode,
                  message
                };
              })
            };

            if (serviceMessage?.data) {
              handleEditMessage(payload);
            } else {
              handleCreateMessage(payload);
            }
          }}
          enableReinitialize>
          {({ values, setFieldValue, errors, touched, isValid, dirty, setFieldTouched }) => (
            <Form className='flex flex-col gap-y-6'>
              <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
                {getLanguageOptionsForServiceMessage().map(({ code, label }) => (
                  <Input
                    type='text'
                    key={code}
                    id={`values.${code}`}
                    label={label}
                    value={values.values[code]}
                    isRequired
                    handleChange={(val) => setFieldValue(`values.${code}`, val)}
                    onBlur={() => setFieldTouched(`values.${code}`, true)}
                    isInvalid={
                      !!getIn(errors, `values.${code}`) && !!getIn(touched, `values.${code}`)
                    }
                    errorMessage={
                      getIn(touched, `values.${code}`) ? getIn(errors, `values.${code}`) : undefined
                    }
                  />
                ))}
              </div>

              <div className='flex flex-col gap-y-2 md:w-1/2 md:pr-2'>
                <SingleSelect
                  id='backgroundColor'
                  label={t('backgroundColor')}
                  isRequired
                  isClearable
                  options={backgroundColorOptions}
                  value={
                    backgroundColorOptions.find((o) => o.value === values.backgroundColor) ?? null
                  }
                  handleChange={(value) => {
                    setFieldValue('backgroundColor', value?.value);
                  }}
                  isInvalid={!!errors.backgroundColor && touched.backgroundColor}
                  errorMessage={touched.backgroundColor ? errors.backgroundColor : undefined}
                  onBlur={() => setFieldTouched('backgroundColor', true)}
                />
              </div>

              <div className='mt-6 flex justify-end gap-2'>
                {serviceMessage?.data ? (
                  <>
                    <Button
                      variant={ButtonVariant.QUATERNARY}
                      className='w-full md:w-auto'
                      handleOnClick={handleRemoveMessage}
                      disabled={deleteServiceMessageMutation.isLoading}>
                      {t('removeMessage')}
                    </Button>
                    <Button
                      type='submit'
                      disabled={!(isValid && dirty)}
                      variant={ButtonVariant.PRIMARY}
                      className='px-8 py-2'>
                      {t('editMessage')}
                    </Button>
                  </>
                ) : (
                  <Button
                    type='submit'
                    disabled={!(isValid && dirty)}
                    variant={ButtonVariant.PRIMARY}
                    className='px-8 py-2'>
                    {t('addMessage')}
                  </Button>
                )}
              </div>
            </Form>
          )}
        </Formik>
      )}
    </div>
  );
};
